import { useMemo } from "react";
import { Link, useParams, useLocation } from "wouter";
import { useDatabase } from "@/hooks/useDatabase";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, ExternalLink, FlaskConical, AlertTriangle, TrendingUp, Sparkles } from "lucide-react";

export default function StudyDetail() {
  const { id } = useParams();
  const [, setLocation] = useLocation();
  const { database, loading, error } = useDatabase();

  const studyData = useMemo(() => {
    if (!database || !id) return null;

    const study = database.studies.find(s => s.study_id === id);
    if (!study) return null;

    const intervention = database.interventions.find(i => i.study_id === id);
    const outcomes = database.outcomes.filter(o => o.study_id === id);
    const studyCompounds = database.study_compounds.filter(sc => sc.study_id === id);
    const studyConditions = database.study_conditions.filter(sc => sc.study_id === id);
    const safety = database.safety_data.find(s => s.study_id === id);
    const marketing = database.marketing_angles.find(m => m.study_id === id);

    const mushroom = intervention ? database.mushrooms.find(m => m.mushroom_id === intervention.mushroom_id) : null;
    const compounds = studyCompounds.map(sc => {
      const compound = database.compounds.find(c => c.compound_id === sc.compound_id);
      return { ...sc, compound };
    });
    const conditions = studyConditions.map(sc => {
      const condition = database.conditions.find(c => c.condition_id === sc.condition_id);
      return { ...sc, condition };
    });

    return {
      study,
      intervention,
      outcomes,
      compounds,
      conditions,
      safety,
      marketing,
      mushroom
    };
  }, [database, id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#F4F2EC] to-[#F4F2EC]">
        <div className="text-center">
          <FlaskConical className="w-16 h-16 mx-auto mb-4 text-[#A98A2C] animate-pulse" />
          <p className="text-lg text-gray-600">Loading study details...</p>
        </div>
      </div>
    );
  }

  if (error || !studyData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-orange-100">
        <div className="text-center">
          <p className="text-lg text-red-600">Study not found</p>
          <Link href="/">
            <Button className="mt-4">Back to Database</Button>
          </Link>
        </div>
      </div>
    );
  }

  const { study, intervention, outcomes, compounds, conditions, safety, marketing, mushroom } = studyData;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F4F2EC] via-[#F4F2EC] to-[#F4F2EC]">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-[#E4E1D8] sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <Link href="/">
            <Button variant="ghost" className="gap-2 mb-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Database
            </Button>
          </Link>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-5xl">
        {/* Study Header */}
        <Card className="mb-8 border-[#E4E1D8] shadow-lg">
          <CardHeader>
            <div className="flex items-start gap-4 mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-3">
                  <Badge variant="outline" className="bg-[#F4F2EC] text-[#2A2A28] border-[#E4E1D8]">
                    {mushroom?.common_name || "Unknown"}
                  </Badge>
                  <Badge variant="outline" className="bg-[#F4F2EC] text-[#8A6F22] border-[#E4E1D8]">
                    {study.study_type}
                  </Badge>
                  <span className="text-lg">{"⭐".repeat(study.quality_score)}</span>
                </div>
                <CardTitle className="text-2xl mb-3">{study.title}</CardTitle>
                <CardDescription className="text-base">
                  {study.authors}
                </CardDescription>
                <div className="flex items-center gap-4 mt-3 text-sm text-gray-600">
                  <span>{study.journal}</span>
                  <span>•</span>
                  <span>{study.year}</span>
                  {study.pmid !== "Not specified" && (
                    <>
                      <span>•</span>
                      <a 
                        href={`https://pubmed.ncbi.nlm.nih.gov/${study.pmid}/`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-[#A98A2C] hover:underline"
                      >
                        PubMed: {study.pmid}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-[#F4F2EC] border border-[#E4E1D8] rounded-lg p-4">
              <p className="font-semibold text-[#2A2A28] mb-2">🔬 Key Finding:</p>
              <p className="text-[#2A2A28] text-lg">{study.key_finding_summary}</p>
            </div>
          </CardHeader>
        </Card>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="border-[#E4E1D8]">
            <CardContent className="pt-6 text-center">
              <div className="text-2xl font-bold text-[#A98A2C]">
                {study.sample_size || "N/A"}
              </div>
              <div className="text-sm text-gray-600">Participants</div>
            </CardContent>
          </Card>
          <Card className="border-[#E4E1D8]">
            <CardContent className="pt-6 text-center">
              <div className="text-2xl font-bold text-[#A98A2C]">
                {study.duration}
              </div>
              <div className="text-sm text-gray-600">Duration</div>
            </CardContent>
          </Card>
          <Card className="border-[#E4E1D8]">
            <CardContent className="pt-6 text-center">
              <div className="text-2xl font-bold text-[#A98A2C]">
                {intervention?.dosage || "N/A"}
              </div>
              <div className="text-sm text-gray-600">Dosage</div>
            </CardContent>
          </Card>
          <Card className="border-[#E4E1D8]">
            <CardContent className="pt-6 text-center">
              <div className="text-2xl font-bold text-[#A98A2C]">
                {safety?.adverse_event_count || 0}
              </div>
              <div className="text-sm text-gray-600">Adverse Events</div>
            </CardContent>
          </Card>
        </div>

        {/* Marketing Angle */}
        {marketing && (
          <Card className="mb-8 border-amber-200 bg-amber-50/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-amber-600" />
                Marketing Angles
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="font-semibold text-amber-900 mb-1">Hero Stat:</p>
                <p className="text-lg text-amber-800">{marketing.hero_stat}</p>
              </div>
              <div>
                <p className="font-semibold text-amber-900 mb-1">Story Hook:</p>
                <p className="text-amber-800">{marketing.story_hook}</p>
              </div>
              <div>
                <p className="font-semibold text-amber-900 mb-1">Visual Opportunity:</p>
                <p className="text-amber-800">{marketing.visual_opportunity}</p>
              </div>
              {marketing.content_tags && marketing.content_tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {marketing.content_tags.map(tag => (
                    <Badge key={tag} variant="outline" className="bg-amber-100 text-amber-800 border-amber-300">
                      #{tag}
                    </Badge>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Detailed Information Tabs */}
        <Tabs defaultValue="intervention" className="mb-8">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="intervention">Intervention</TabsTrigger>
            <TabsTrigger value="outcomes">Outcomes</TabsTrigger>
            <TabsTrigger value="safety">Safety</TabsTrigger>
            <TabsTrigger value="details">Details</TabsTrigger>
          </TabsList>

          <TabsContent value="intervention">
            <Card className="border-[#E4E1D8]">
              <CardHeader>
                <CardTitle>Intervention Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="font-semibold text-gray-700 mb-1">Mushroom Species:</p>
                  <p className="text-gray-900">{mushroom?.common_name} ({mushroom?.scientific_name})</p>
                </div>
                <div>
                  <p className="font-semibold text-gray-700 mb-1">Extraction Method:</p>
                  <p className="text-gray-900">{intervention?.extraction_method.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
                </div>
                <div>
                  <p className="font-semibold text-gray-700 mb-1">Dosage:</p>
                  <p className="text-gray-900">{intervention?.dosage}</p>
                </div>
                <div>
                  <p className="font-semibold text-gray-700 mb-1">Form:</p>
                  <p className="text-gray-900">{intervention?.form.replace(/\b\w/g, l => l.toUpperCase())}</p>
                </div>
                <div>
                  <p className="font-semibold text-gray-700 mb-1">Standardization:</p>
                  <p className="text-gray-900">{intervention?.standardization}</p>
                </div>

                <div className="pt-4 border-t">
                  <p className="font-semibold text-gray-700 mb-3">Bioactive Compounds:</p>
                  <div className="space-y-2">
                    {compounds.map(c => (
                      <div key={c.id} className="flex items-center justify-between p-3 bg-[#F4F2EC] rounded-lg">
                        <div>
                          <p className="font-medium text-gray-900">{c.compound?.name}</p>
                          <p className="text-sm text-gray-600">{c.compound?.chemical_name}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-gray-900">{c.concentration}</p>
                          {c.is_primary && (
                            <Badge variant="outline" className="text-xs">Primary</Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="outcomes">
            <Card className="border-[#E4E1D8]">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  Study Outcomes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {outcomes.map((outcome, index) => (
                    <div key={outcome.outcome_id} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <Badge variant={outcome.outcome_type === 'primary' ? 'default' : 'outline'} className="mb-2">
                            {outcome.outcome_type.toUpperCase()}
                          </Badge>
                          <p className="font-semibold text-gray-900">{outcome.measure}</p>
                        </div>
                        {outcome.statistical_significance && (
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
                            Significant
                          </Badge>
                        )}
                      </div>
                      <p className="text-gray-700 mb-2">{outcome.result}</p>
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        {outcome.change_percent !== null && (
                          <span>Change: {outcome.change_percent > 0 ? '+' : ''}{outcome.change_percent}%</span>
                        )}
                        {outcome.p_value !== null && (
                          <span>p = {outcome.p_value}</span>
                        )}
                      </div>
                      {outcome.clinical_significance && (
                        <p className="mt-2 text-sm text-gray-600 italic">{outcome.clinical_significance}</p>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="safety">
            <Card className="border-[#E4E1D8]">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  Safety Profile
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <p className="font-semibold text-green-900 mb-1">Adverse Events:</p>
                  <p className="text-green-800">{safety?.adverse_events}</p>
                </div>
                {safety?.serious_adverse_events && safety.serious_adverse_events !== "None" && (
                  <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                    <p className="font-semibold text-red-900 mb-1">Serious Adverse Events:</p>
                    <p className="text-red-800">{safety.serious_adverse_events}</p>
                  </div>
                )}
                <div>
                  <p className="font-semibold text-gray-700 mb-1">Interactions:</p>
                  <p className="text-gray-900">{safety?.interactions}</p>
                </div>
                <div>
                  <p className="font-semibold text-gray-700 mb-1">Contraindications:</p>
                  <p className="text-gray-900">{safety?.contraindications}</p>
                </div>
                {safety?.safety_notes && (
                  <div className="p-4 bg-[#F4F2EC] rounded-lg border border-[#E4E1D8]">
                    <p className="font-semibold text-[#2A2A28] mb-1">Additional Safety Notes:</p>
                    <p className="text-[#8A6F22]">{safety.safety_notes}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="details">
            <Card className="border-[#E4E1D8]">
              <CardHeader>
                <CardTitle>Study Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="font-semibold text-gray-700 mb-1">Study Design:</p>
                  <p className="text-gray-900">{study.study_type} • {study.blinding}</p>
                </div>
                <div>
                  <p className="font-semibold text-gray-700 mb-1">Population:</p>
                  <p className="text-gray-900">{study.demographics}</p>
                </div>
                <div>
                  <p className="font-semibold text-gray-700 mb-1">Conditions Studied:</p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {conditions.map(c => (
                      <Badge key={c.id} variant="outline" className="bg-[#F4F2EC] text-[#8A6F22] border-[#E4E1D8]">
                        {c.condition?.name}
                      </Badge>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="font-semibold text-gray-700 mb-1">Abstract:</p>
                  <p className="text-gray-900 leading-relaxed">{study.abstract}</p>
                </div>
                <div>
                  <p className="font-semibold text-gray-700 mb-1">Funding Source:</p>
                  <p className="text-gray-900">{study.funding_source}</p>
                </div>
                <div>
                  <p className="font-semibold text-gray-700 mb-1">Conflicts of Interest:</p>
                  <p className="text-gray-900">{study.conflicts_of_interest}</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
